'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface QualityStandard {
  id: string;
  title: string;
  description: string;
  details: string[];
}

interface HygieneQualityProps {
  className?: string;
}

const HygieneQuality = ({ className = '' }: HygieneQualityProps) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const qualityStandards: QualityStandard[] = [
    {
      id: 'kitchen',
      title: 'Kitchen Hygiene',
      description: 'State-of-the-art kitchen facilities with strict cleanliness protocols',
      details: [
        'Daily deep cleaning and sanitization of all surfaces',
        'Temperature-controlled storage for all ingredients',
        'Separate preparation areas for vegetarian and non-vegetarian items',
        'Regular pest control and maintenance inspections',
        'Staff health monitoring and hygiene training programs',
      ],
    },
    {
      id: 'ingredients',
      title: 'Ingredient Sourcing',
      description: 'Premium quality ingredients from certified suppliers',
      details: [
        'Direct partnerships with local organic farms',
        'Daily freshness checks and quality assessments',
        'Traceability system for all ingredients',
        'No artificial preservatives or harmful additives',
        'Seasonal menu adjustments for peak ingredient quality',
      ],
    },
    {
      id: 'preparation',
      title: 'Food Preparation',
      description: 'Standardized cooking processes ensuring consistency and safety',
      details: [
        'Trained chefs following international culinary standards',
        'Temperature monitoring during cooking and storage',
        'Portion control for consistent quality',
        'Allergen awareness and cross-contamination prevention',
        'Regular taste testing and quality checks',
      ],
    },
    {
      id: 'packaging',
      title: 'Packaging & Delivery',
      description: 'Safe, eco-friendly packaging maintaining food quality',
      details: [
        'Food-grade, leak-proof containers',
        'Temperature-insulated packaging for hot and cold items',
        'Tamper-evident seals on all orders',
        'Biodegradable and recyclable materials',
        'Contactless delivery options available',
      ],
    },
  ];

  const certifications = [
    {
      name: 'FDA Approved',
      icon: 'CheckBadgeIcon',
      description: 'Food and Drug Administration certified kitchen',
    },
    {
      name: 'ISO 22000',
      icon: 'ShieldCheckIcon',
      description: 'International food safety management certification',
    },
    {
      name: 'HACCP',
      icon: 'DocumentCheckIcon',
      description: 'Hazard Analysis Critical Control Points compliant',
    },
    {
      name: 'A+ Rating',
      icon: 'StarIcon',
      description: 'Health Department inspection rating',
    },
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <section className={`bg-muted ${className}`}>
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold text-foreground sm:text-4xl">
            Hygiene & Quality Standards
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
            Your health and safety are our top priorities. We maintain the highest standards of
            hygiene and quality at every step of food preparation and delivery.
          </p>
        </div>

        {/* Certifications */}
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {certifications.map((cert, index) => (
            <div
              key={index}
              className="rounded-lg bg-card p-6 text-center shadow-sm transition-smooth hover-lift"
            >
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                <Icon name={cert.icon} size={32} className="text-success" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">
                {cert.name}
              </h3>
              <p className="text-caption mt-2 text-muted-foreground">{cert.description}</p>
            </div>
          ))}
        </div>

        {/* Quality Standards Accordion */}
        <div className="mt-16 space-y-4">
          {qualityStandards.map((standard) => (
            <div
              key={standard.id}
              className="overflow-hidden rounded-lg bg-card shadow-sm transition-smooth"
            >
              <button
                type="button"
                onClick={() => toggleSection(standard.id)}
                className="flex w-full items-center justify-between px-6 py-4 text-left transition-smooth hover:bg-muted focus-ring"
                aria-expanded={expandedSection === standard.id}
              >
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    {standard.title}
                  </h3>
                  <p className="text-caption mt-1 text-muted-foreground">{standard.description}</p>
                </div>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  className={`text-muted-foreground transition-transform ${
                    expandedSection === standard.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expandedSection === standard.id && (
                <div className="border-t border-border bg-background px-6 py-4">
                  <ul className="space-y-3">
                    {standard.details.map((detail, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Icon
                          name="CheckCircleIcon"
                          size={20}
                          className="mt-0.5 flex-shrink-0 text-success"
                        />

                        <span className="text-sm text-foreground">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Kitchen Gallery */}
        <div className="mt-16">
          <h3 className="text-center font-heading text-2xl font-semibold text-foreground">
            Our Kitchen
          </h3>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Take a look inside our state-of-the-art kitchen facilities
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="overflow-hidden rounded-lg shadow-md transition-smooth hover-lift">
              <AppImage
                src="/assets/kitchen/2.png"
                alt="Professional chef in our kitchen with modern commercial equipment"
                className="h-64 w-full object-cover object-top"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-md transition-smooth hover-lift">
              <AppImage
                src="/assets/kitchen/3.png"
                alt="Fresh ingredients and vegetables in our kitchen"
                className="h-64 w-full object-cover"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-md transition-smooth hover-lift">
              <AppImage
                src="/assets/kitchen/kit1.png"
                alt="Clean organized kitchen storage and cooking stations"
                className="h-64 w-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HygieneQuality;
